import { ChevronRight } from "@procore/core-icons";
import { useCallback, useMemo, useRef } from "react";

import type { ListNode, Node, Tree } from "components/objectTree/types";
import { useHidden } from "contexts/hidden";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { highlightText } from "utils/textHighlight";
import { getGroupStateForFilteredTree } from "utils/treeHelpers";

import { INDEX_SUFFIX_PATTERN, ITEM_HEIGHT_IOS } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { formatNodeDisplayName } from "../utils/formatting";
import { SwipeableRow } from "./SwipeableRow";
import { VisibilityToggleButton } from "./VisibilityToggleButton";

export type MobileSearchResultsProps = {
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number>;
    searchTerm?: string;
    onSelectGroup: (path: string) => void;
};

function deriveBreadcrumb(path: string): string | null {
    const parts = path
        .split("/")
        .slice(0, -1)
        .map((s) => s.replace(INDEX_SUFFIX_PATTERN, "$1"));
    if (parts.length === 0) {
        return null;
    }
    if (parts.length <= 2) {
        return parts.join(" > ");
    }
    return `${parts[0]} > ... > ${parts[parts.length - 1]}`;
}

export function MobileSearchResults({
    tree,
    filteredObjectIds,
    searchTerm = "",
    onSelectGroup,
}: MobileSearchResultsProps) {
    const activeRowCloseRef = useRef<(() => void) | null>(null);

    const handleRowOpen = useCallback((closeFn: () => void) => {
        if (activeRowCloseRef.current !== closeFn) {
            activeRowCloseRef.current?.();
        }
        activeRowCloseRef.current = closeFn;
    }, []);

    const matches = useMemo(
        () =>
            Object.values(tree)
                .filter((n): n is Node<ModelTreeNodeData> => n.path !== "" && filteredObjectIds.has(n.data.object.id))
                .sort((a, b) => a.path.localeCompare(b.path)),
        [tree, filteredObjectIds],
    );

    return (
        <div style={{ height: "100%", overflowY: "auto" }}>
            {matches.map((node) => (
                <SearchResultRow
                    key={node.path}
                    node={node}
                    tree={tree}
                    filteredObjectIds={filteredObjectIds}
                    searchTerm={searchTerm}
                    onSelectGroup={onSelectGroup}
                    onOpen={handleRowOpen}
                />
            ))}
        </div>
    );
}

function SearchResultRow({
    node,
    tree,
    filteredObjectIds,
    searchTerm,
    onSelectGroup,
    onOpen,
}: {
    node: Node<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
    filteredObjectIds: Set<number>;
    searchTerm: string;
    onSelectGroup: (path: string) => void;
    onOpen: (close: () => void) => void;
}) {
    const hidden = useHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;

    const item = useMemo((): ListNode<ModelTreeNodeData> => ({ node, parent: null, level: 0 }), [node]);
    const hiddenState = useMemo(() => {
        if (isolationVisibleIds) {
            return invertGroupState(getGroupStateForFilteredTree(isolationVisibleIds, item, filteredObjectIds, tree));
        }
        return getGroupStateForFilteredTree(hidden.ids, item, filteredObjectIds, tree);
    }, [hidden.ids, isolationVisibleIds, item, tree, filteredObjectIds]);
    const isHidden =
        hiddenState === "all" || (!isolationVisibleIds && !node.isLeaf && hidden.ids.has(node.data.object.id));

    const breadcrumb = deriveBreadcrumb(node.path);
    const displayName = formatNodeDisplayName(item);
    const isDrillable = !node.isLeaf;
    const contentOpacity = isHidden ? 0.4 : 1;

    return (
        <SwipeableRow node={node} onOpen={onOpen} filteredObjectIds={filteredObjectIds}>
            <div
                onClick={isDrillable ? () => onSelectGroup(node.path) : undefined}
                style={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: ITEM_HEIGHT_IOS,
                    padding: "0 16px",
                    gap: 10,
                    borderBottom: "1px solid rgba(172, 181, 185, 0.32)",
                    userSelect: "none",
                    cursor: isDrillable ? "pointer" : "default",
                }}
            >
                <span
                    style={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        opacity: contentOpacity,
                    }}
                >
                    {breadcrumb && (
                        <span
                            data-testid="search-result-breadcrumb"
                            style={{
                                fontSize: 12,
                                color: "#6B7280",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {breadcrumb}
                        </span>
                    )}
                    <span
                        style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                            overflowWrap: "anywhere",
                            fontSize: 18,
                            fontWeight: 600,
                            color: "#171a1c",
                            letterSpacing: "-0.28px",
                            lineHeight: "20px",
                        }}
                    >
                        {searchTerm ? highlightText(displayName, searchTerm) : displayName}
                    </span>
                </span>
                <VisibilityToggleButton node={node} tree={tree} filteredObjectIds={filteredObjectIds} />
                {isDrillable && <ChevronRight style={{ flexShrink: 0, width: 20, height: 20, color: "#ACB5B9" }} />}
            </div>
        </SwipeableRow>
    );
}
